import { ChartNumbersType, GeneratedChartData } from 'common/types/chart';
import { FilterNames } from 'common/types/filters';
import { CSVExportRequestPayload, FormValues } from 'common/types/export';
import { AVERAGE_LABEL } from 'modules/charts/utils/chartDataConstants';

interface ChartExportRowData {
  brand: string;
  country: string;
  kpi: string;
  segment: string;
  wave: string;
  value: number;
  populationNumberRaw: number;
  moe: number;
}

interface CSVExportOptions {
  filename?: string;
  showPopulationNumbers: boolean;
}

const FILTER_TO_EXPORT_FIELD_MAP: Partial<Record<FilterNames, keyof ChartExportRowData>> = {
  [FilterNames.BRAND]: 'brand',
  [FilterNames.COUNTRY]: 'country',
  [FilterNames.KPI]: 'kpi',
  [FilterNames.SEGMENT]: 'segment',
  [FilterNames.WAVE]: 'wave',
};

const getAverageExportField = (filterName?: FilterNames): keyof ChartExportRowData | undefined => {
  if (!filterName) {
    return undefined;
  }

  return FILTER_TO_EXPORT_FIELD_MAP[filterName];
};

const applyAverageLabelForField = (row: ChartExportRowData, field: keyof ChartExportRowData | undefined): void => {
  if (!field) {
    return;
  }

  if (field === 'brand' || field === 'country' || field === 'kpi' || field === 'segment' || field === 'wave') {
    row[field] = AVERAGE_LABEL;
  }
};

export const exportChartDataToCSV = (
  chartData: GeneratedChartData,
  chartName: string,
  showPopulationNumbers: boolean,
  chartNumbersType: ChartNumbersType,
) => {
  const showTotalNumbers = chartNumbersType === ChartNumbersType.TOTAL;
  exportChartToCSV(transformChartDataToExportFormat(chartData, showTotalNumbers), {
    filename: chartName,
    showPopulationNumbers,
  });
};

const transformChartDataToExportFormat = (chartData: GeneratedChartData, showTotalNumbers: boolean): ChartExportRowData[] => {
  const result: ChartExportRowData[] = [];
  const averageRowExportField = getAverageExportField(chartData.indexBy);
  const averageKeyExportField = getAverageExportField(chartData.secondDimension);

  // Transform the data
  chartData.data.forEach(item => {
    const isAverageRow = item.groupLabel === AVERAGE_LABEL || item.groupId === AVERAGE_LABEL;

    chartData.keys.forEach(key => {
      const baseKey = key;
      const filterLabels = item[`${baseKey}_filterLabels` as 'value_0_filterLabels'];
      const isAverageKey =
        item[`${baseKey}_label` as 'value_0_label'] === AVERAGE_LABEL ||
        item[`${baseKey}_secondDimension` as 'value_0_secondDimension'] === AVERAGE_LABEL;

      if (!filterLabels) return;

      const chartDataExportItem: ChartExportRowData = {
        brand: filterLabels.brand,
        country: filterLabels.country,
        kpi: filterLabels.kpi,
        segment: filterLabels.segment,
        wave: filterLabels.wave,
        value: showTotalNumbers ? item[`${baseKey}_totalValue` as 'value_0_totalValue'] : item[baseKey as 'value_0'],
        populationNumberRaw: item[`${baseKey}_populationNumberRaw` as 'value_0_populationNumberRaw'],
        moe: showTotalNumbers ? item[`${baseKey}_totalMoeValue` as 'value_0_totalMoeValue'] : item[`${baseKey}_moe` as 'value_0_moe'],
      };

      if (isAverageRow) {
        applyAverageLabelForField(chartDataExportItem, averageRowExportField);
      }

      if (isAverageKey) {
        applyAverageLabelForField(chartDataExportItem, averageKeyExportField);
      }

      result.push(chartDataExportItem);
    });
  });

  // Sort only by brand, maintaining original order for equal brands
  return result.sort((a, b) => a.brand.localeCompare(b.brand));
};

// Constants
const CSV_HEADERS = ['Brand', 'Segment', 'KPI', 'Country', 'Wave', 'Value (%)', 'Population', 'Margin of Error (%)'] as const;

const DEFAULT_FILENAME = 'chart_export.csv';
const CSV_MIME_TYPE = 'text/csv;charset=utf-8;';

// Helper functions
// A leading =, +, -, @, tab or CR makes spreadsheets evaluate the cell as a formula; the apostrophe keeps it text.
const FORMULA_TRIGGER = /^[=+\-@\t\r]/;

const escapeCSVField = (field: string): string => {
  const safeField = FORMULA_TRIGGER.test(field) ? `'${field}` : field;
  return `"${safeField.replace(/"/g, '""')}"`;
};

const formatPopulation = (value: number | undefined, showPopulation: boolean): number | string => {
  if (!showPopulation || !value) return '';
  return Math.round(value);
};

const convertRowToCSV = (row: ChartExportRowData, showPopulationNumbers: boolean): string => {
  const fields = [
    escapeCSVField(row.brand),
    escapeCSVField(row.segment),
    escapeCSVField(row.kpi),
    escapeCSVField(row.country),
    escapeCSVField(row.wave),
    row.value, // Value (%) - as number
    formatPopulation(row.populationNumberRaw, showPopulationNumbers),
    row.moe, // Margin of Error (%) - as number
  ];

  return fields.join(',');
};

const createCSVContent = (data: ChartExportRowData[], showPopulationNumbers: boolean): string => {
  const rows = [CSV_HEADERS.join(','), ...data.map(row => convertRowToCSV(row, showPopulationNumbers))];

  return rows.join('\n');
};

const downloadBlob = (blob: Blob, filename: string): void => {
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);

  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url); // Clean up the URL object
};

// Main export function
export const exportChartToCSV = (data: ChartExportRowData[], options: CSVExportOptions): void => {
  const filename = options.filename || DEFAULT_FILENAME;
  const csvContent = createCSVContent(data, options.showPopulationNumbers);
  const blob = new Blob([csvContent], { type: CSV_MIME_TYPE });

  downloadBlob(blob, filename);
};

export type CSVCell = string | number;

/** For charts whose columns don't fit the fixed per-data-point layout above: text is quoted, numbers stay raw. */
export const exportRowsToCSV = (rows: CSVCell[][], filename?: string): void => {
  const csvContent = rows.map(row => row.map(cell => (typeof cell === 'number' ? cell : escapeCSVField(cell))).join(',')).join('\n');
  const blob = new Blob([csvContent], { type: CSV_MIME_TYPE });

  downloadBlob(blob, filename || DEFAULT_FILENAME);
};

export const normalizeCSVExportData = (formData: FormValues): CSVExportRequestPayload => {
  const normalizePayload = (data: Record<string, boolean>): (string | number)[] => {
    const dataIdList = [];

    for (const item in data) {
      if (data[item]) {
        let index;

        if (item.startsWith('wave_')) {
          index = Number(item.slice(5));
        } else {
          index = item;
        }

        dataIdList.push(index);
      }
    }

    return dataIdList;
  };

  const payload = {
    recipient_email: formData.email,
    study_ids: normalizePayload(formData.studies),
    wave_ids: normalizePayload(formData.waves),
  };

  return payload;
};
